// 2026-10-18：界面文案独立翻译，保留业务值及用户输入，提供英文短名称和维语排版。
import { ref, toRaw } from 'vue'
import catalog from './catalog.json'
import shortCatalog from './short-catalog.json'

export type UiLanguage = 'zh' | 'en' | 'ug'
type Entry = [string, string]
// JSON数组长度由check-ui-i18n覆盖检查校验，TypeScript默认仅推断为string[]。
const words = catalog as unknown as Record<string, Entry>
const shortWords = shortCatalog as unknown as Record<string, Entry>
const normalizedWords: Record<string, Entry> = {}
const normalizeText = (value: string) => value.replace(/\s+/g, ' ').trim()
Object.keys(words).forEach(key => { normalizedWords[normalizeText(key)] = words[key] })
export const normalizeLanguage = (value: unknown): UiLanguage => value === 'en' || value === 'ug' ? value : 'zh'
export const displayLanguage = ref<UiLanguage>('zh')
export function setDisplayLanguage(language: unknown): UiLanguage {
  const next = normalizeLanguage(language)
  displayLanguage.value = next
  if (typeof document !== 'undefined') {
    document.documentElement.lang = next === 'zh' ? 'zh-CN' : next
    document.documentElement.dir = next === 'ug' ? 'rtl' : 'ltr'
    document.documentElement.dataset.uiLanguage = next
  }
  return next
}
export function translateUi(value: unknown, compact = false, language: UiLanguage = displayLanguage.value): any {
  if (typeof value !== 'string' || language === 'zh') return value
  const text = value.trim()
  const entry = (compact && shortWords[text]) || words[text] || normalizedWords[normalizeText(text)]
  if (!entry) {
    // 2026-10-18：验证码倒计时只替换展示模板，秒数及定时器状态不变。
    const countdown = /^(\d+)秒后重新获取$/.exec(text)
    if (countdown) return translateUi('{seconds}秒后重新获取', false, language).replace('{seconds}', countdown[1])
    // 2026-10-18：购买期限展示保留完整月数，不改变套餐期限或支付字段。
    const term = /^(\d+)个月$/.exec(text)
    if (term) return translateUi('{months}个月', false, language).replace('{months}', term[1])
    return value
  }
  return value.replace(text, () => entry[language === 'en' ? 0 : 1])
}
export function translateMessages(source: any, language: UiLanguage): any {
  if (typeof source === 'string') return translateUi(source, false, language)
  if (Array.isArray(source)) return source.map(value => translateMessages(value, language))
  if (!source || typeof source !== 'object') return source
  return Object.fromEntries(Object.entries(source).map(([key, value]) => [key, translateMessages(value, language)]))
}
// 2026-10-18：完整动态界面句子使用序号占位符，金额、编号和自定义文本按原值插入。
export function formatUi(template: string, values: unknown[], language: UiLanguage = displayLanguage.value): string {
  return String(translateUi(template, false, language)).replace(/\{(\d+)\}/g, (placeholder, index) => Number(index) < values.length ? String(values[Number(index)]) : placeholder)
}

// 原始表格/选项/校验对象只登记静态文案，不改写可提交的业务对象。
const uiObjects = new WeakMap<object, Record<string, string>>()
export function markUiObject<T extends object>(value: T, fields: Record<string, string>): T {
  uiObjects.set(value, fields)
  return value
}
export function translateUiField(value: any, key: string): any {
  if (!value || typeof value !== 'object') return value?.[key]
  const fields = uiObjects.get(toRaw(value))
  return fields && fields[key] === value[key] ? translateUi(value[key]) : value[key]
}
export function translateUiFields(value: any): any {
  if (Array.isArray(value)) return value.map(translateUiFields)
  if (!value || typeof value !== 'object' || typeof value === 'function') return value
  const fields = uiObjects.get(toRaw(value))
  const output = { ...value }
  if (fields) Object.keys(fields).forEach(key => {
    if (value[key] === fields[key]) output[key] = translateUi(value[key])
  })
  // 2026-10-18：表单规则按字段映射到数组；递归翻译登记文案，不改验证器/枚举值。
  for (const key of Object.keys(value)) if (Array.isArray(value[key]) || key === 'rules') output[key] = translateUiFields(value[key])
  return output
}
